import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { PackageCard, AssociationNotice } from './PortalCards';
import { fullName, polishDate, lessonTypeLabel } from './clientPortalSelectors';
import ClientBilling from './ClientBilling';
import { portalAttendanceOutcome } from '../../../LambdaFunctions/shared/portalAttendance';

export default function ClientPortalSection({ section, data, snapshot, selectedStudentId, onBillingYear }) {
  const { t: tr, i18n } = useTranslation();
  const fallback = (snapshot.students || []).length === 1 ? data.unassociated : { packages: [], courses: [], materials: [] };
  const lessonTypes = snapshot.lessonTypes || [];
  if (section === 'packages') {
    return (
      <section className="client-portal__card">
        {data.unknownAssociations.packages && <AssociationNotice />}
        {data.packages.map(item => <PackageCard key={item.id} item={item} lessonTypes={lessonTypes} />)}
        {fallback.packages.length > 0 && (
        <div><h2>{tr("client:dataNotAssignedToAStudent_20")}</h2>
          {fallback.packages.map(item => <PackageCard key={item.id} item={item} lessonTypes={lessonTypes} />)}
        </div>
        )}
        {!data.packages.length && !data.unknownAssociations.packages && <p>{tr("client:noPackages_7")}</p>}
      </section>
    );
  }
  if (section === 'billing') {
    return (
      <ClientBilling
        key={(snapshot.client || {}).id}
        ledger={data.ledger}
        client={snapshot.client || {}}
        invoices={data.invoices}
        estimate={snapshot.billingEstimate}
        billingYear={snapshot.billingYear}
        onBillingYear={onBillingYear}
      />
    );
  }
  if (section === 'summaries') {
    const students = (snapshot.students || []).filter(student => !selectedStudentId || student.id === selectedStudentId);
    return (
      <section className="client-portal__card client-portal__lesson-summaries">
        {!students.length && <p>{tr("client:noAssignedStudents_0")}</p>}
        {students.map(student => (
          <div key={student.id}><h2>{fullName(student)}</h2>
            {!data.summaries.some(item => item.studentId === student.id) && <p>{tr("client:noCompletedLessons_28")}</p>}
            {data.summaries.filter(item => item.studentId === student.id).map(item => (
              <article key={`${item.lessonId}-${item.studentId}`} className="client-portal__lesson-summary">
                <p className="text-muted">{polishDate(item.date, false, i18n.language)}{item.startTime ? `, ${item.startTime}` : ''}</p>
                {item.status === 'cancelled' && <p>{tr("manage:lessonCancelled_54")}</p>}
                {item.status === 'noSummary' && <p>{tr("manage:noSummaryHasBeenAdded_88")}</p>}
                {item.status === 'summarized' && <p>{item.lessonSummary}</p>}
              </article>
            ))}
          </div>
        ))}
      </section>
    );
  }
  if (section === 'attendance') {
    return (
      <section className="client-portal__card">
        {data.attendance ? <p>{tr("client:attendance_29")} {data.attendance.present}/{data.attendance.total} ({data.attendance.rate}%)</p> : <p>{tr("client:noAttendanceData_30")}</p>}
        {data.attendanceLessons.map(item => (
          <article className="client-portal__detail-row" key={`${item.id}-${item.studentId}`}>
            <div>{polishDate(item.date, false, i18n.language)} {item.startTime}</div><div>{item.studentName}</div><div>{tr(`client:attendanceOutcome.${portalAttendanceOutcome(item)}`)}</div>
          </article>
        ))}
      </section>
    );
  }
  if (section === 'history') {
    return (
      <>
        <section className="client-portal__card"><h2>{tr("client:lessons_33")}</h2>{!data.historyLessons.length && <p>{tr("client:noLessonHistory_34")}</p>}
          {data.historyLessons.map(item => (
            <article key={`${item.id}-${item.studentId}`} className="client-portal__detail-row">
              <div>{polishDate(item.date, false, i18n.language)} {item.startTime}</div><div>{item.studentName}</div><div>{lessonTypeLabel(item.classTypeId, lessonTypes, i18n.language)}</div>
            </article>
          ))}
        </section>
        <section className="client-portal__card"><h2>{tr("client:courses_35")}</h2>{data.unknownAssociations.courses && <AssociationNotice />}
          {!data.courses.length && !data.unknownAssociations.courses && <p>{tr("client:noCourseHistory_36")}</p>}
          {fallback.courses.length > 0 && (
          <div><h2>{tr("client:dataNotAssignedToAStudent_20")}</h2>
            {fallback.courses.map(item => (
              <article className="client-portal__detail-row" key={item.id}>
                <div>{lessonTypeLabel(item.classTypeId, lessonTypes, i18n.language)}</div>
                {item.fromDate && <div>{tr("manage:from_295")} {polishDate(item.fromDate, false, i18n.language)}</div>}{item.tillDate && <div>{tr("manage:to_296")} {polishDate(item.tillDate, false, i18n.language)}</div>}
              </article>
            ))}
          </div>
          )}
          {data.courses.map(item => (
            <article className="client-portal__detail-row" key={item.id}><div>{item.name || lessonTypeLabel(item.classTypeId, lessonTypes, i18n.language)}</div>
              {item.fromDate && <div>{tr("manage:from_295")} {polishDate(item.fromDate, false, i18n.language)}</div>}{item.tillDate && <div>{tr("manage:to_296")} {polishDate(item.tillDate, false, i18n.language)}</div>}
            </article>
          ))}
        </section>
      </>
    );
  }
  return (
    <section className="client-portal__card">
      {data.unknownAssociations.materials && <AssociationNotice />}
      {!data.materials.length && !data.unknownAssociations.materials && <p>{tr("client:noMaterials_37")}</p>}
      {fallback.materials.length > 0 && (
      <div><h2>{tr("client:dataNotAssignedToAStudent_20")}</h2>
        {fallback.materials.map(item => <article className="client-portal__detail-row" key={item.id}><h3>{item.title}</h3>{item.description && <p>{item.description}</p>}</article>)}
      </div>
      )}
      {data.materials.map(item => <article className="client-portal__detail-row" key={item.id}><h2>{item.title}</h2>{item.description && <p>{item.description}</p>}</article>)}
    </section>
  );
}
ClientPortalSection.propTypes = {
  section: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  snapshot: PropTypes.object.isRequired,
  selectedStudentId: PropTypes.string,
  onBillingYear: PropTypes.func,
};
ClientPortalSection.defaultProps = { selectedStudentId: null, onBillingYear: null };
